import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  UserManager,
  WebStorageStateStore,
  User,
  UserManagerSettings,
} from "oidc-client";
import { toast } from "react-toastify";
import { parseQuery } from "ufo";

import { RootState } from "../store";
import fetchSingleton from "@/lib/fetchSingleton";
import { humanizeError } from "@/lib/humanizeError";
import { forceDropAllUnloadBlockers } from "@/lib/useUnloadBlocker";
import type { ApiResponseDto, ApiUserT } from "@/types/api-types";

const userManager = globalThis.window
  ? (() => {
      const config: UserManagerSettings = {
        authority: process.env.NEXT_PUBLIC_OAUTH_AUTRORITY_URL as string,
        client_id: process.env.NEXT_PUBLIC_OAUTH_CLIENT_ID as string,
        redirect_uri:
          globalThis.window.location.origin +
          (process.env.NEXT_PUBLIC_OAUTH_REDIRECT_RELATIVE_URL || ""),
        silent_redirect_uri:
          globalThis.window.location.origin +
          (process.env.NEXT_PUBLIC_OAUTH_REDIRECT_RELATIVE_URL || ""),
        post_logout_redirect_uri:
          globalThis.window.location.origin +
          (process.env.NEXT_PUBLIC_OAUTH_REDIRECT_RELATIVE_URL || ""),
        // In real request {token id_token} replaced with token for cognito, because we need id_token,
        // but cognito don't understand request with id_token
        response_type: "token id_token",
        scope: "profile email openid",
        filterProtocolClaims: true,
        // loadUserInfo: true,
        automaticSilentRenew: true,
        userStore: new WebStorageStateStore({ store: globalThis.window.localStorage }),
      };

      return new UserManager(config);
    })()
  : undefined;

type JWTCognitoPayload = {
  sub: string;
  username: string;
  "cognito:groups": "Admin"[];
};

interface AuthState {
  session: (User & JWTCognitoPayload & { user: ApiUserT }) | undefined;
  status: "authenticated" | "loading" | "unauthenticated";
  error: string | undefined;
}

const initialState: AuthState = {
  session: undefined,
  status: "loading",
  error: undefined,
};

export const initAuth = createAsyncThunk("auth/init", async (_, { dispatch }) => {
  const window = globalThis.window;

  if (!window) return;

  try {
    if (window.sessionStorage.getItem("shouldShowSessionKilledWarning")) {
      window.sessionStorage.removeItem("shouldShowSessionKilledWarning");
      toast.warning(
        "You have been logged out due to a broken or expired session. Please log in again.",
      );
    }

    let signinRedirectCallbackCalled: boolean = false;

    if (window.location.hash.includes("id_token")) {
      await userManager?.signinRedirectCallback();
      signinRedirectCallbackCalled = true;
    }

    // http://localhost:3000/#error_description=invalid_scope&state=033b42b9daad4016b8e522bdce431362&error=invalid_request
    if (window.location.hash.includes("error_description")) {
      const parsed = parseQuery(window.location.hash.replace("#", ""));
      dispatch(setStatus("unauthenticated"));
      dispatch(setError(`Authentication failed: ${parsed.error_description}`));

      toast.error(humanizeError(parsed, "Auth"));
      return;
    }

    const maybeSession = await userManager?.getUser();

    if (!maybeSession) {
      dispatch(setStatus("unauthenticated"));
      dispatch(setError(undefined));
      if (signinRedirectCallbackCalled) {
        window.sessionStorage.setItem("shouldShowSessionKilledWarning", "true");
        await dispatch(signOut());
      }
      return;
    }

    const checkToken = (await fetchSingleton(
      process.env.NEXT_PUBLIC_API_URL + "/api/internal/v1/auth/validate-token",
      {
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${maybeSession.id_token}`,
        },
      },
    )) as ApiResponseDto<void>;

    if (checkToken.status !== "OK") {
      dispatch(setStatus("unauthenticated"));
      dispatch(setError(undefined));
      if (signinRedirectCallbackCalled) {
        window.sessionStorage.setItem("shouldShowSessionKilledWarning", "true");
        await dispatch(signOut());
      }
      return;
    }

    const userData = (await fetchSingleton(
      process.env.NEXT_PUBLIC_API_URL + "/api/v1/user/me",
      {
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${maybeSession.id_token}`,
        },
      },
    )) as ApiResponseDto<ApiUserT>;

    if (userData.status !== "OK") {
      dispatch(setStatus("unauthenticated"));
      dispatch(setError(undefined));
      if (signinRedirectCallbackCalled) {
        window.sessionStorage.setItem("shouldShowSessionKilledWarning", "true");
        await dispatch(signOut());
      }
      return;
    }

    const sessionData = { ...maybeSession, user: userData.list[0] };

    logIdTokenInDevEnv(maybeSession);

    dispatch(setSession(sessionData));
    dispatch(setStatus("authenticated"));
  } catch (error) {
    dispatch(setStatus("unauthenticated"));
    dispatch(setError(JSON.stringify(error)));
  }
});

function logIdTokenInDevEnv(maybeSession: { id_token: string }) {
  if (
    maybeSession.id_token &&
    (process.env.NODE_ENV === "development" ||
      globalThis.window?.location.hostname.startsWith("localhost") ||
      globalThis.window?.location.hostname.startsWith("dev"))
  ) {
    console.info(
      "id_token [showed only in dev mode and in prod mode on dev domain]",
      maybeSession.id_token,
    );
  }
}

export const signIn = createAsyncThunk("auth/login", async (_, { dispatch }) => {
  try {
    if (!userManager) return;
    const authRequest = await userManager?.createSigninRequest({
      extraQueryParams: {
        /**
         * Select account won't work normally in cognito
         * thet is why we use a prompt login and login hint to prevent login into another account
         * when session is active
         */
        // prompt: "login",
        login_hint: (await userManager.getUser())?.profile?.email || "",
      },
    });
    const authRequestUrlAdaptedForCognito = authRequest?.url?.replace(
      /token.+id_token/,
      "token",
    );
    forceDropAllUnloadBlockers();
    globalThis.window.location.href = authRequestUrlAdaptedForCognito;
    await new Promise(resolve => setTimeout(resolve, 5000)); // Wait for real redirect
  } catch (error) {
    dispatch(setError(`Failed to load user session + ${String(error)}`));
    throw error;
  }
});

export const signOut = createAsyncThunk("auth/logout", async () => {
  if (!userManager) return;
  await new Promise(resolve => setTimeout(resolve, 1500)); // Fake sleep for better ui
  const authRequest = await userManager?.createSignoutRequest({
    extraQueryParams: {
      client_id: process.env.NEXT_PUBLIC_OAUTH_CLIENT_ID as string,
    },
  });

  const requestUrlAdaptedForCognito = authRequest?.url?.replace(
    /post_logout_redirect_uri/,
    "logout_uri",
  );
  globalThis.window.localStorage.clear(); // TODO Resolve this hack
  globalThis.window.location.replace(requestUrlAdaptedForCognito);
  await new Promise(resolve => setTimeout(resolve, 5000)); // Wait for real redirect
});

export const triggerGotUnauthorized = createAsyncThunk(
  "auth/triggerGotUnauthorized",
  async (message: string | undefined, { dispatch, getState }) => {
    // @ts-expect-error // TODO Add getState definition
    if (getState()?.auth?.status !== "unauthenticated") {
      dispatch(setStatus("unauthenticated"));
      dispatch(setError(message));
      // TODO Refactor this, move notification to right place
      toast.error(humanizeError(message));
    }
  },
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    $disfigureSession: state => {
      if (state.session) {
        state.session.id_token = "disfigured-token";
      }
    },
    setSession: (state, action) => {
      state.session = action.payload;
      state.status = action.payload ? "authenticated" : "unauthenticated";
    },
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: state => {
      state.error = undefined;
    },
  },
  extraReducers: builder => {
    builder.addCase(signIn.pending, state => {
      state.error = undefined;
    });
  },
});

// Selectors
export const selectAuth = (state: RootState) => state.auth;

export const { $disfigureSession, setSession, setStatus, setError, clearError } =
  authSlice.actions;
export default authSlice.reducer;
